import { TrendingUp, Users, Rocket, DollarSign } from "lucide-react";

const reasons = [
  {
    icon: TrendingUp,
    title: "Massive Market Shift",
    description: "Companies are migrating their most critical Oracle databases to OCI (Oracle Cloud Infrastructure) for cost savings, performance, and agility. Your on-prem expertise is the foundation; cloud skills make you indispensable for this journey."
  },
  {
    icon: Users,
    title: "High Demand, Low Supply",
    description: "There is a significant skills gap. There are far more on-prem DBAs than there are certified OCI Database Professionals. By certifying now, you position yourself in a high-demand, low-competition niche."
  },
  {
    icon: Rocket,
    title: "Future-Proof Your Career",
    description: "The role of the DBA is evolving from routine maintenance to strategic cloud architecture, automation, and governance. This certification is your blueprint for that evolution."
  },
  {
    icon: DollarSign,
    title: "Unlock Higher Earning Potential",
    description: "Certified OCI professionals command significant salary premiums. This investment in your skills pays for itself many times over."
  },
];

const techStack = [
  "Oracle Database", "Oracle E-Business Suite", "Oracle JD Edwards",
  "Oracle PeopleSoft Enterprise", "Oracle Hyperion", "Oracle SOA Suite",
  "Oracle WebLogic Server", "Kubernetes", "Docker", "NoSQL Database",
];

export default function WhyNowSection() {
  return (
    <section id="whynow" data-testid="whynow-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111118]">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="font-mono text-xs text-[#22D3EE] uppercase tracking-widest mb-2">// opportunity</p>
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#F4F4F5] mb-4">
            Why Now is the Perfect Time to Upskill
          </h2>
          <p className="text-base text-[#A1A1AA] max-w-3xl">
            The digital world is accelerating its move to the cloud, and Oracle is at the forefront. For the traditional Oracle DBA, this isn't a threat &mdash; it's the greatest career advancement opportunity in a decade. Don't just keep the lights on; become the strategic cloud expert your company needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-12">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <div
                key={index}
                data-testid={`whynow-reason-${index}`}
                className="p-6 bg-[#16161F] border border-[#27272A] rounded-md hover:border-[#E32636]/30 transition-all duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-[#E32636]/10 border border-[#E32636]/20 group-hover:bg-[#E32636]/20 transition-colors">
                    <Icon size={18} className="text-[#E32636]" />
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] text-base font-semibold text-[#F4F4F5] mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* On-Premise to OCI tech stack */}
        <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md">
          <h3 className="font-['Space_Grotesk'] text-sm font-semibold text-[#22D3EE] uppercase tracking-wider mb-4">
            On-Premise to Oracle Cloud Infrastructure
          </h3>
          <p className="text-sm text-[#A1A1AA] mb-4">
            This course covers migration paths from on-premise enterprise systems to OCI, including:
          </p>
          <div className="flex flex-wrap gap-2">
            {techStack.map((tech) => (
              <span
                key={tech}
                className="px-3 py-1.5 text-xs font-mono text-[#22D3EE] bg-[#0A0A0F] border border-[#27272A] rounded-md"
              >
                {tech}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#71717A]">
            OCI Services covered: ID & Access Management &middot; Compute (VM | BM | Dedicated) &middot; Networking (VCN | Gateways | LB) &middot; Storage (Block | File | Object) &middot; Database System
          </p>
        </div>
      </div>
    </section>
  );
}
